// Impact = what changed for mothers and babies because of the reminders. Counts only.
// The same rules run on the server (functions/src/stats.ts) for real clinics.
import type { Baby, Call, Dose, Flag, Impact, Mother } from '../types';

export const STAFF_MINUTES_PER_CALL = 4; // reception's time to dial, wait, talk and note one reminder
const ANSWERED = ['confirmed', 'question_flagged', 'opted_out', 'retry_scheduled'];

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return Math.round(s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2);
};

export function computeImpact(d: { mothers: Mother[]; babies: Baby[]; doses: Dose[]; calls: Call[]; flags: Flag[] }, today = new Date().toISOString().slice(0, 10)): Impact {
  const motherOfBaby = new Map(d.babies.map((b) => [b.id, b.motherId]));
  const callsBy = new Map<string, Call[]>();
  d.calls.forEach((c) => callsBy.set(c.motherId, [...(callsBy.get(c.motherId) ?? []), c]));

  // Recovered: given late, and the mother had a reminder call after the due date, before it was given.
  const dosesRecovered = d.doses.filter((x) => {
    if (x.status !== 'attended') return false;
    // Only count when we know the real date it was given, and it was late.
    const given = x.attendedAt?.slice(0, 10);
    if (!given || given <= x.dueDate) return false;
    const calls = callsBy.get(motherOfBaby.get(x.babyId) ?? '') ?? [];
    return calls.some((c) => c.startedAt.slice(0, 10) >= x.dueDate && c.startedAt.slice(0, 10) <= given);
  }).length;

  const dueSoFar = d.doses.filter((x) => x.dueDate <= today);
  const babiesUpToDate = d.babies.filter((b) => dueSoFar.filter((x) => x.babyId === b.id).every((x) => x.status === 'attended')).length;

  const urgent = d.flags.filter((f) => f.urgency === 'urgent');
  const ack = urgent.filter((f) => f.acknowledgedAt).map((f) => (new Date(f.acknowledgedAt!).getTime() - new Date(f.raisedAt).getTime()) / 60000).filter((m) => m >= 0);

  return {
    dosesRecovered,
    babiesTotal: d.babies.length,
    babiesUpToDate,
    urgentFlags: urgent.length,
    urgentHandled: urgent.filter((f) => f.status !== 'open' || f.acknowledgedAt).length,
    medianAckMinutes: median(ack),
    mothersReached: new Set(d.calls.filter((c) => ANSWERED.includes(c.outcome)).map((c) => c.motherId)).size,
    callsMade: d.calls.length,
    staffMinutesSaved: d.calls.length * STAFF_MINUTES_PER_CALL,
  };
}
